// C:\Projects\texe\texe-web\src\context\AuthProvider.tsx
import { createContext, useContext, useEffect, useState } from "react";
import { apiGet, apiPost, clearToken, setToken } from "../lib/api";
import { refreshAccess, getToken } from "../lib/api";

export type User = {
  id: string;
  username: string;
  email: string;
  createdAt?: string;

  bio?: string | null;
  favoriteQuote?: string | null;
  website?: string | null;

  avatarUrl?: string | null;
  avatarUrlSmall?: string | null;

  preferredLanguage?: string | null;

  /** ⬅️ EKLENDİ: admin bayrağı (backend /api/auth/* döndürür) */
  isAdmin?: boolean;
  
  /** SavWar Terfi Puanı */
  savwarTP?: number;
  
  /** SavWar Rütbe seviyesi (1-23) */
  savwarRank?: number;
};

type AuthContextType = {
  user: User | null;
  loading: boolean;
  register: (p: { username: string; email: string; password: string }) => Promise<void>;
  login: (p: { emailOrUsername: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | null>(null);

// Promise'i ms içinde tamamlarsa döndür, yoksa undefined
async function withTimeout<T>(p: Promise<T>, ms = 6000): Promise<T | undefined> {
  return await Promise.race([
    p.catch(() => undefined),
    new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), ms)),
  ]);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Açılış: oturumu ve access token'ı toparla
  useEffect(() => {
    let ignore = false;

    (async () => {
      try {
        // Önce token varlığını kontrol et - token yoksa API çağrısı yapma
        if (!getToken()) {
          // Token yoksa refresh dene
          try {
            await refreshAccess();
          } catch {
            // Refresh de başarısız - kullanıcı yok
            if (!ignore) {
              setUser(null);
              setLoading(false);
            }
            return;
          }
        }

        // Token varsa veya refresh başarılıysa kullanıcı bilgilerini al
        // Öncelik: /api/auth/me (isAdmin alanını kesin döndürüyor)
        const me = await withTimeout(apiGet<{ ok: true; user: User }>("/api/auth/me"), 6000);

        if (me?.ok && me.user && !ignore) {
          setUser(me.user);
        } else {
          // Fallback: eski route
          const prof = await withTimeout(apiGet<{ ok: true; user: User }>("/api/profile/me"), 6000);
          if (prof?.ok && prof.user && !ignore) {
            setUser(prof.user);
          } else if (!ignore) {
            setUser(null);
          }
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    })();

    return () => { ignore = true; };
  }, []);

  async function refreshMe() {
    // Token yoksa önce refresh dene
    if (!getToken()) {
      try {
        await refreshAccess();
      } catch {
        setUser(null);
        return;
      }
    }

    const me = await withTimeout(apiGet<{ ok: true; user: User }>("/api/auth/me"), 6000);
    if (me?.ok && me.user) {
      setUser(me.user);
      return;
    }
    const prof = await withTimeout(apiGet<{ ok: true; user: User }>("/api/profile/me"), 6000);
    if (prof?.ok && prof.user) {
      setUser(prof.user);
      return;
    }
    setUser(null);
  }

  async function register(p: { username: string; email: string; password: string }) {
    const data = await apiPost<{
      ok: boolean;
      access?: string;
      user?: User;
    }>("/api/auth/register", {
      username: p.username,
      email: p.email,
      password: p.password,
    });

    if (data.access) setToken(data.access);
    if (data.user) setUser(data.user);
    else await refreshMe();
  }

  async function login(p: { emailOrUsername: string; password: string }) {
    console.log("[AUTH] Starting login...");
    const data = await apiPost<{
      ok: boolean;
      access?: string;
      token?: string;
      accessToken?: string;
      refresh?: string;
      user?: User; // ⬅️ backend artık isAdmin ile gönderiyor
    }>("/api/auth/login", {
      emailOrUsername: p.emailOrUsername,
      password: p.password,
    });

    console.log("[AUTH] Login response:", {
      ok: data.ok,
      hasAccess: !!data.access,
      hasToken: !!data.token,
      hasAccessToken: !!data.accessToken,
      hasRefresh: !!data.refresh,
      hasUser: !!data.user,
      refreshPreview: data.refresh?.substring(0, 20) + "..."
    });

    const t = data.access || data.token || data.accessToken;
    if (t) {
      console.log("[AUTH] Setting access token...");
      setToken(t);
    }
    // Refresh token cookie backend tarafından ayarlanır (httpOnly); body ile gelmeyebilir.
    if (!data.refresh) {
      console.log("[AUTH] No refresh field (cookie-based).");
    }

    if (data.user) setUser(data.user); // ⬅️ isAdmin burada state'e girer
    else await refreshMe();
  }

  async function logout() {
    try {
      await apiPost("/api/auth/logout", {});
    } finally {
      clearToken();
      setUser(null);
    }
  }

  return (
    <AuthContext.Provider value={{ user, loading, register, login, logout, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
